import { z } from 'zod/v3';

// Schema for scalar JSON values (matches Ty.Scalar type)
const ScalarValueSchema: z.ZodType<any> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.date(),
    z.null(),
    z.record(ScalarValueSchema),
    z.array(ScalarValueSchema),
  ])
);

const ColWhenSchema = z.union([
  z.literal('hourly'),
  z.literal('daily'),
  z.literal('never'),
  z.object({
    cron: z.string(),
  }),
]);

// Tiptap document structure schemas
const MarkSchema: z.ZodType<any> = z.object({
  type: z.string(),
  attrs: z.record(z.any()).optional(),
});

const TextNodeSchema: z.ZodType<any> = z.object({
  type: z.literal('text'),
  text: z.string(),
  marks: z.array(MarkSchema).optional(),
});

const ContentNodeSchema: z.ZodType<any> = z.lazy(() =>
  z.object({
    type: z.string(),
    attrs: z.record(z.any()).optional(),
    content: z.array(z.union([TextNodeSchema, ContentNodeSchema])).optional(), // TextNode FIRST so it matches before ContentNode
    marks: z.array(MarkSchema).optional(),
  })
);

const TiptapDocumentSchema = z.object({
  type: z.literal('doc'),
  content: z.array(z.union([TextNodeSchema, ContentNodeSchema])).optional(), // TextNode FIRST
});

const agentSchema = z.object({
  outputTy: z.string(),
  editorState: TiptapDocumentSchema,
  model: z.string(),
  schedule: ColWhenSchema.optional(),
  temperature: z.number().optional(),
  maxOutputTokens: z.number().optional(),
  topP: z.number().optional(),
  topK: z.number().optional(),
  frequencyPenalty: z.number().optional(),
  presencePenalty: z.number().optional(),
});

export const relColSchema = z.union([
  z.object({
    t: z.literal('eraql'),
    source: z.string(),
  }),
  agentSchema.extend({
    t: z.literal('llm'),
    version: z.literal('v2').optional().default('v2'),
  }),
  z.object({
    t: z.literal('llm'),
    prompt: z.string(),
    systemMessage: z.string(),
    outputTy: z.string(), //serialized Ty.displayTy(Ty.ExtendedAttributeType)
    imageUrl: z.string().nullable(),
    when: ColWhenSchema,
    model: z.string(),
    temperature: z.number().optional(),
    maxOutputTokens: z.number().optional(),
    tools: z.array(z.string()),
    durable: z.boolean().optional(),
    version: z.literal('v1').optional().default('v1'),
  }),
  z.object({
    t: z.literal('workflow'),
    source: z.string(),
    toolSymbolMap: z.record(
      z.object({
        toolName: z.string(),
        toolId: z.string(),
      })
    ),
    when: ColWhenSchema,
  }),
]);

const createColumnSchema = z.object({
  t: z.literal('create-column'),
  relName: z.string(),
  name: z.string(),
  config: relColSchema,
});

const createExprSchema = z.object({
  t: z.literal('create-expr'),
  symbol: z.string(),
  source: z.string(),
  metadata: z.record(ScalarValueSchema).optional(),
});

const createDocumentSchema = z.object({
  t: z.literal('create-document'),
  symbol: z.string(),
  source: TiptapDocumentSchema,
  metadata: z.record(ScalarValueSchema).optional(),
});

const createAgentSchema = agentSchema.extend({
  t: z.literal('create-agent'),
  symbol: z.string(),
});

const createRelSchema = z.object({
  t: z.literal('create-rel'),
  symbol: z.string(),
  source: z.string(),
  where: z.string().nullish(),
  pk: z.string().nullish(),
});

const updateExprSchema = z.object({
  t: z.literal('update-expr'),
  symbol: z.string(),
  source: z.string().optional(),
  metadata: z.record(ScalarValueSchema).optional(),
});

const updateDocumentSchema = z.object({
  t: z.literal('update-document'),
  symbol: z.string(),
  source: TiptapDocumentSchema,
  metadata: z.record(ScalarValueSchema).optional(),
});

const updateAgentSchema = z.object({
  t: z.literal('update-agent'),
  symbol: z.string(),
  outputTy: z.string().optional(),
  editorState: TiptapDocumentSchema.optional(),
  model: z.string().optional(),
  schedule: ColWhenSchema.optional(),
});

const updateRelSchema = z.object({
  t: z.literal('update-rel'),
  symbol: z.string(),
  source: z.string().optional(),
  pk: z.string().nullish(),
  where: z.string().nullish(),
});

const updateOrReplaceColumnSchema = z.object({
  t: z.enum(['update-column', 'replace-column']),
  relName: z.string(),
  name: z.string(),
  config: relColSchema,
});

const deleteSchema = z.object({
  t: z.literal('delete'),
  symbol: z.string(),
});

const deleteColSchema = z.object({
  t: z.literal('delete-col'),
  relName: z.string(),
  name: z.string(),
});

const toolNodeDefSchema = z.object({
  t: z.literal('ffi'),
  type: z.string(),
  toolId: z.string(),
  toolConnectionId: z.string(),
});

const createToolSchema = z.object({
  t: z.literal('create-tool'),
  symbol: z.string(),
  def: toolNodeDefSchema,
});

const updateToolSchema = z.object({
  t: z.literal('update-tool'),
  symbol: z.string(),
  def: toolNodeDefSchema,
});

// Combined schemas
const addOpSchema = z.discriminatedUnion('t', [
  createToolSchema,
  createColumnSchema,
  createExprSchema,
  createDocumentSchema,
  createAgentSchema,
  createRelSchema,
]);

const renameOpSchema = z.object({
  t: z.literal('rename'),
  symbol: z.string(),
  newSymbol: z.string(),
});

const renameColOpSchema = z.object({
  t: z.literal('rename-col'),
  relName: z.string(),
  name: z.string(),
  newName: z.string(),
});

const lockSymbolSchema = z.object({
  t: z.literal('lock'),
  symbol: z.string(),
});

const unlockSymbolSchema = z.object({
  t: z.literal('unlock'),
  symbol: z.string(),
});

const duplicateSchema = z.object({
  t: z.literal('duplicate'),
  symbol: z.string().optional(),
  newSymbol: z.string(),
  // For column duplication
  relName: z.string().optional(),
  name: z.string().optional(),
  newName: z.string().optional(),
});

// Main OperationData schema
export const OperationDataSchema = z.discriminatedUnion('t', [
  // Create operations
  createToolSchema,
  createColumnSchema,
  createExprSchema,
  createDocumentSchema,
  createAgentSchema,
  createRelSchema,
  // Update operations
  updateToolSchema,
  updateExprSchema,
  updateDocumentSchema,
  updateAgentSchema,
  updateRelSchema,
  updateOrReplaceColumnSchema,
  // Delete operation
  deleteSchema,
  deleteColSchema,
  // Rename operation
  renameOpSchema,
  renameColOpSchema,
  // Lock operation
  lockSymbolSchema,
  // Unlock operation
  unlockSymbolSchema,
  // Duplicate operation
  duplicateSchema,
]);

export const CommitSchema = z.object({
  message: z.string().nullable(),
  operations: z.array(OperationDataSchema),
  actorId: z.string(),
  timestamp: z.date({ coerce: true }),
  // The index this commit is intended to be appended at (for optimistic concurrency control)
  optimisticIndex: z.number().optional(),
});

const SavedCommitSchema = CommitSchema.extend({
  index: z.number(),
});

const GraphBuildFailureSchema = z.object({
  error: z.object({
    t: z.string(),
    message: z.string().optional(),
  }),
});

export const CommitSchemas = {
  OperationDataSchema,
  CommitSchema,
  GraphBuildFailureSchema,
  updateToolSchema,
  updateOrReplaceColumnSchema,
  updateExprSchema,
  updateDocumentSchema,
  updateAgentSchema,
  updateRelSchema,
  createToolSchema,
  createColumnSchema,
  createExprSchema,
  createDocumentSchema,
  createAgentSchema,
  createRelSchema,
  deleteSchema,
  deleteColSchema,
  renameOpSchema,
  renameColOpSchema,
  addOpSchema,
  lockSymbolSchema,
  unlockSymbolSchema,
  duplicateSchema,
  relColSchema,
};
